/**
 * Recreates the original inline tab/step logic from the static HTML
 * and exposes the expected globals so existing onclick handlers keep working.
 */
import Tesseract from 'tesseract.js'
import { datConfigApi, type DatChoice } from './datConfigApi'
import { saveInquiryDraft, submitInquiryDraft } from './inquiryDraft'

const showToast = (message, tone = 'info') => {
  if (typeof document === 'undefined') return
  const toast = document.createElement('div')
  toast.textContent = message
  toast.style.position = 'fixed'
  toast.style.top = '16px'
  toast.style.right = '16px'
  toast.style.padding = '12px 14px'
  toast.style.borderRadius = '12px'
  toast.style.color = '#fff'
  toast.style.zIndex = '9999'
  toast.style.fontSize = '14px'
  toast.style.boxShadow = '0 10px 30px rgba(0,0,0,0.12)'
  toast.style.backgroundColor =
    tone === 'error' ? '#dc2626' : tone === 'success' ? '#16a34a' : '#2563eb'
  document.body.appendChild(toast)
  setTimeout(() => {
    toast.style.opacity = '0'
    toast.style.transition = 'opacity 200ms ease'
    setTimeout(() => toast.remove(), 220)
  }, 2200)
}

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

const OCR_LANG = 'deu+eng'
const FIN_WHITELIST = 'ABCDEFGHJKLMNPRSTUVWXYZ0123456789'
const HSN_WHITELIST = `${FIN_WHITELIST}abcdefghijklmnopqrstuvwxyz`
const DEFAULT_PSM = Tesseract?.PSM?.SPARSE_TEXT ?? 6
const SINGLE_LINE_PSM = Tesseract?.PSM?.SINGLE_LINE ?? 7
let ocradLoader = null

const ensureOcrad = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('OCRAD_UNAVAILABLE'))
  }
  if (window.OCRAD) return Promise.resolve(window.OCRAD)
  if (!ocradLoader) {
    ocradLoader = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = '/vendor/ocrad.js'
      script.async = true
      script.onload = () => {
        if (window.OCRAD) {
          resolve(window.OCRAD)
        } else {
          ocradLoader = null
          reject(new Error('OCRAD_GLOBAL_MISSING'))
        }
      }
      script.onerror = () => {
        ocradLoader = null
        reject(new Error('OCRAD_LOAD_FAILED'))
      }
      document.body.appendChild(script)
    })
  }
  return ocradLoader
}

const replaceCharacters = (value = '', map = {}) =>
  value
    .split('')
    .map((char) => map[char] ?? char)
    .join('')

const DIGIT_LOOKALIKES = {
  O: '0',
  Q: '0',
  D: '0',
  U: '0',
  'Ü': '0',
  I: '1',
  L: '1',
  B: '8',
  S: '5',
  Z: '2',
  T: '7',
}

const ALNUM_LOOKALIKES = {
  ...DIGIT_LOOKALIKES,
  'Ä': 'A',
  'Ö': 'O',
}

const REGION_HINTS = {
  fin: [
    { x: 0.1, y: 0.12, width: 0.8, height: 0.18 },
    { x: 0.08, y: 0.34, width: 0.84, height: 0.2 },
    { x: 0.05, y: 0.58, width: 0.9, height: 0.24 },
  ],
  hsn: {
    hsn: [
      { x: 0.04, y: 0.2, width: 0.36, height: 0.18 },
      { x: 0.04, y: 0.32, width: 0.36, height: 0.18 },
    ],
    tsn: [
      { x: 0.42, y: 0.2, width: 0.34, height: 0.18 },
      { x: 0.42, y: 0.32, width: 0.34, height: 0.18 },
    ],
  },
}

const STRATEGY_HINTS = {
  ocrad: ' (Fallback-Methode)',
  'tesseract-region': ' (gezielter Ausschnitt)',
  'tesseract-binarized': ' (optimiert)',
  'tesseract-raw': ' (Rohbild)',
  'ai-gpt-nano': ' (KI)',
}

// Be slightly tolerant here so we don't discard a good AI result; server already hard-validates.
const isValidFin = (value = '') => /^[A-Z0-9]{11,20}$/.test(value.toUpperCase())
const isValidHsn = (value = '') => /^\d{4}$/.test(value)
const isValidTsn = (value = '') => /^[A-Z0-9]{3}$/.test(value.toUpperCase())

const tryAiScan = async (file, type) => {
  // Use raw data URL to avoid over-processing before the model sees it
  const dataUrl = await fileToDataUrl(file)
  const res = await fetch('/api/scan', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ imageBase64: dataUrl, type }),
  })
  if (!res.ok) throw new Error(`ai_scan_failed_${res.status}`)
  const json = await res.json()
  const fin = json.fin?.toString()?.toUpperCase() || ''
  const hsn = json.hsn?.toString() || ''
  const tsn = json.tsn?.toString()?.toUpperCase() || ''

  console.info('[AI scan result]', { type, fin, hsn, tsn })

  if (type === 'fin') {
    if (isValidFin(fin)) {
      return { payload: { fin }, strategy: 'ai-gpt-nano' }
    }
    return null
  }

  if (isValidHsn(hsn) && isValidTsn(tsn)) {
    return { payload: { hsn, tsn }, strategy: 'ai-gpt-nano' }
  }
  return null
}

const sanitizeNumeric = (value = '') =>
  replaceCharacters(value.toUpperCase(), DIGIT_LOOKALIKES).replace(/\D/g, '').slice(0, 4)

const sanitizeTsn = (value = '') =>
  replaceCharacters(value.toUpperCase(), ALNUM_LOOKALIKES)
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 3)

const candidateFromTexts = (texts = [], pattern, normalize) => {
  for (const text of texts) {
    if (!text) continue
    const match = text.match(pattern)
    if (match?.[0]) {
      const cleaned = normalize ? normalize(match[0]) : match[0]
      if (cleaned?.length) return cleaned
    }
  }
  return ''
}

const cropCanvasRegion = (sourceCanvas, region) => {
  if (!sourceCanvas || typeof document === 'undefined') return null
  const { width, height } = sourceCanvas
  const sx = Math.max(0, Math.floor(width * region.x))
  const sy = Math.max(0, Math.floor(height * region.y))
  const sw = Math.max(1, Math.floor(width * region.width))
  const sh = Math.max(1, Math.floor(height * region.height))
  const canvas = document.createElement('canvas')
  canvas.width = sw
  canvas.height = sh
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.drawImage(sourceCanvas, sx, sy, sw, sh, 0, 0, sw, sh)
  return canvas
}

const runRegionHintOcr = async (canvas, region, whitelist) => {
  const cropped = cropCanvasRegion(canvas, region)
  if (!cropped) return ''
  const dataUrl = cropped.toDataURL('image/png')
  const { data } = await Tesseract.recognize(dataUrl, OCR_LANG, {
    tessedit_char_whitelist: whitelist,
    preserve_interword_spaces: '1',
    psm: SINGLE_LINE_PSM,
  })
  return data.text || ''
}

const runTargetedRegions = async (canvasOrList, type) => {
  const canvases = Array.isArray(canvasOrList)
    ? canvasOrList.filter(Boolean)
    : [canvasOrList].filter(Boolean)
  for (const canvas of canvases) {
    try {
      if (type === 'fin') {
        const texts = []
        for (const hint of REGION_HINTS.fin) {
          const text = await runRegionHintOcr(canvas, hint, FIN_WHITELIST)
          if (text) texts.push(text)
        }
        const fin = extractFin(texts.join('\n'))
        if (fin) {
          return { payload: { fin }, variant: 'region' }
        }
      } else {
        const hsnTexts = []
        const tsnTexts = []
        for (const hint of REGION_HINTS.hsn.hsn) {
          const text = await runRegionHintOcr(canvas, hint, '0123456789OQDSBL')
          if (text) hsnTexts.push(text)
        }
        for (const hint of REGION_HINTS.hsn.tsn) {
          const text = await runRegionHintOcr(canvas, hint, HSN_WHITELIST)
          if (text) tsnTexts.push(text)
        }
        const hsn = sanitizeNumeric(candidateFromTexts(hsnTexts, /\d{3,5}/, sanitizeNumeric))
        const tsn = sanitizeTsn(candidateFromTexts(tsnTexts, /[A-Z0-9]{2,4}/i, sanitizeTsn))
        if (hsn && hsn.length === 4 && tsn && tsn.length === 3) {
          return { payload: { hsn, tsn }, variant: 'region' }
        }
      }
    } catch (err) {
      console.warn('Targeted OCR regions failed', err)
    }
  }
  return null
}

const enhanceImageData = (imageData) => {
  if (!imageData?.data) return imageData
  const data = imageData.data
  const contrast = 1.25
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i]
    const g = data[i + 1]
    const b = data[i + 2]
    const gray = 0.299 * r + 0.587 * g + 0.114 * b
    const contrasted = Math.min(255, Math.max(0, (gray - 128) * contrast + 128))
    const threshold = contrasted > 150 ? 255 : 0
    data[i] = threshold
    data[i + 1] = threshold
    data[i + 2] = threshold
  }
  return imageData
}

const preprocessImage = async (file) => {
  const dataUrl = await fileToDataUrl(file)
  if (typeof document === 'undefined') {
    return { dataUrl, canvas: null, originalUrl: dataUrl, rawCanvas: null }
  }
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const scale = Math.min(2, 1200 / Math.max(img.width, img.height))
      const canvas = document.createElement('canvas')
      canvas.width = Math.max(1, Math.floor(img.width * scale))
      canvas.height = Math.max(1, Math.floor(img.height * scale))
      const ctx = canvas.getContext('2d', { willReadFrequently: true })
      if (!ctx) {
        resolve({ dataUrl, canvas: null, originalUrl: dataUrl, rawCanvas: null })
        return
      }
      ctx.imageSmoothingEnabled = true
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

      // Preserve an unprocessed copy for a second OCR pass
      const rawCanvas = document.createElement('canvas')
      rawCanvas.width = canvas.width
      rawCanvas.height = canvas.height
      const rawCtx = rawCanvas.getContext('2d')
      rawCtx?.drawImage(canvas, 0, 0)
      const originalUrl = rawCanvas.toDataURL('image/png')

      // Binarize for the main OCR pass
      const processed = enhanceImageData(ctx.getImageData(0, 0, canvas.width, canvas.height))
      ctx.putImageData(processed, 0, 0)
      resolve({ dataUrl: canvas.toDataURL('image/png'), canvas, originalUrl, rawCanvas })
    }
    img.onerror = (err) => reject(err)
    img.crossOrigin = 'Anonymous'
    img.src = dataUrl
  })
}

const runPrimaryOcr = async (dataUrl, type) => {
  const whitelist = type === 'fin' ? FIN_WHITELIST : HSN_WHITELIST
  const { data } = await Tesseract.recognize(dataUrl, OCR_LANG, {
    tessedit_char_whitelist: whitelist,
    preserve_interword_spaces: '1',
    psm: DEFAULT_PSM,
  })
  return data.text || ''
}

const runFallbackOcr = async (canvas) => {
  if (!canvas) throw new Error('NO_CANVAS_FOR_FALLBACK')
  const ocrad = await ensureOcrad()
  if (typeof ocrad !== 'function') throw new Error('OCRAD_NOT_FUNCTION')
  return ocrad(canvas) || ''
}

const interpretOcrText = (type, text) => {
  if (!text) return null
  if (type === 'fin') {
    const fin = extractFin(text)
    return fin ? { fin } : null
  }
  const { hsn, tsn } = extractHsnTsn(text)
  if (hsn && tsn) return { hsn, tsn }
  return null
}

const extractVehicleData = async (file, type) => {
  const prep = await preprocessImage(file)

  const primaryVariants = [
    { url: prep.dataUrl, label: 'binarized' },
    { url: prep.originalUrl, label: 'raw' },
  ]

  for (const variant of primaryVariants) {
    if (!variant.url) continue
    try {
      const text = await runPrimaryOcr(variant.url, type)
      const result = interpretOcrText(type, text)
      if (result) {
        return { payload: result, strategy: `tesseract-${variant.label}` }
      }
    } catch (err) {
      console.warn(`Primary OCR (${variant.label}) failed`, err)
    }
  }

  const regionResult = await runTargetedRegions([prep.canvas, prep.rawCanvas], type)
  if (regionResult) {
    return { payload: regionResult.payload, strategy: 'tesseract-region' }
  }

  try {
    const fallbackText = await runFallbackOcr(prep.canvas)
    const fallbackResult = interpretOcrText(type, fallbackText)
    if (fallbackResult) {
      return { payload: fallbackResult, strategy: 'ocrad' }
    }
  } catch (err) {
    console.warn('Fallback OCR failed', err)
  }

  throw new Error('NO_OCR_RESULT')
}

const fillFinFromScan = (fin, context) => {
  if (!fin) return false
  const finInputId = context === 'mobile' ? 'mobile-fin-input' : 'desktop-fin-input'
  const input = document.getElementById(finInputId)
  if (input) {
    input.value = fin
    context === 'mobile'
      ? showNextStep('fin-step-1')
      : showNextStep('desktop-fin-step-1', 'desktop-tab-content-window')
    return true
  }
  return false
}

const fillHsnFromScan = ({ hsn, tsn }, context) => {
  if (!(hsn && tsn)) return false
  const hsnId = context === 'mobile' ? 'mobile-hsn' : 'desktop-hsn'
  const tsnId = context === 'mobile' ? 'mobile-tsn' : 'desktop-tsn'
  const hsnEl = document.getElementById(hsnId)
  const tsnEl = document.getElementById(tsnId)
  if (hsnEl && tsnEl) {
    hsnEl.value = hsn
    tsnEl.value = tsn
    context === 'mobile' ? checkHsnTsn() : checkHsnTsnDesktop()
    return true
  }
  return false
}

const setScanningUiState = (type, context, isLoading) => {
  // Update desktop content radii based on active tab
  const container = document.getElementById('desktop-tab-content-window')
  if (container && context === 'desktop') {
    container.classList.remove(
      'desktop-tab-content-window--fin',
      'desktop-tab-content-window--hsn',
      'desktop-tab-content-window--model'
    )
    const activeTab = document.querySelector('[data-tab-group="desktop"] .tab-btn.active')
    const target = activeTab?.getAttribute('data-tab-target')
    if (target === 'fin') {
      container.classList.add('desktop-tab-content-window--fin')
    } else if (target === 'hsn') {
      container.classList.add('desktop-tab-content-window--hsn')
    } else {
      container.classList.add('desktop-tab-content-window--model')
    }
  }
  const ids =
    type === 'fin'
      ? {
          btn: context === 'mobile' ? 'mobile-fin-scan' : 'desktop-fin-scan',
          inputs: context === 'mobile' ? ['mobile-fin-input'] : ['desktop-fin-input'],
        }
      : {
          btn: context === 'mobile' ? 'mobile-hsn-scan' : 'desktop-hsn-scan',
          inputs: context === 'mobile' ? ['mobile-hsn', 'mobile-tsn'] : ['desktop-hsn', 'desktop-tsn'],
        }

  const btnEl = document.getElementById(ids.btn)
  if (btnEl) {
    if (!btnEl.dataset.originalText) btnEl.dataset.originalText = btnEl.textContent || ''
    btnEl.textContent = isLoading ? 'Scanne...' : btnEl.dataset.originalText
    btnEl.disabled = isLoading
    btnEl.classList.toggle('opacity-60', isLoading)
    btnEl.classList.toggle('cursor-not-allowed', isLoading)
  }

  ids.inputs.forEach((id) => {
    const el = document.getElementById(id)
    if (el) {
      el.readOnly = isLoading
      el.classList.toggle('animate-pulse', isLoading)
    }
  })
}

const scanDocumentFile = async (file, type, context, origin = 'upload') => {
  if (!file) return false
  setScanningUiState(type, context, true)
  showToast(
    origin === 'camera'
      ? 'Foto aufgenommen – erkenne Daten...'
      : 'Foto empfangen – erkenne Daten...'
  )
  try {
    // First, try remote AI proxy if available
    try {
      const aiResult = await tryAiScan(file, type)
      if (aiResult) {
        const { payload, strategy } = aiResult
        if (type === 'fin') {
          if (fillFinFromScan(payload.fin, context)) {
            showToast(`FIN erkannt${STRATEGY_HINTS[strategy] ?? ''}`, 'success')
            return true
          }
        } else if (fillHsnFromScan(payload, context)) {
          showToast(`HSN/TSN erkannt${STRATEGY_HINTS[strategy] ?? ''}`, 'success')
          return true
        }
      }
    } catch (aiErr) {
      console.warn('AI scan failed, falling back to local OCR', aiErr)
    }

    const { payload, strategy } = await extractVehicleData(file, type)
    const strategyLabel = STRATEGY_HINTS[strategy] ?? ''
    if (type === 'fin') {
      const filled = fillFinFromScan(payload.fin, context)
      if (filled) {
        showToast(
          `FIN erkannt${strategyLabel}`,
          'success'
        )
        return true
      }
    } else if (fillHsnFromScan(payload, context)) {
      showToast(
        `HSN/TSN erkannt${strategyLabel}`,
        'success'
      )
      return true
    }
    throw new Error('FIELDS_NOT_FILLED')
  } catch (err) {
    console.error('Scan pipeline error', err)
    showToast(
      type === 'fin'
    ? 'FIN konnte nicht erkannt werden – bitte neues Foto versuchen'
    : 'HSN/TSN konnten nicht erkannt werden – bitte neues Foto versuchen',
      'error'
    )
    return false
  } finally {
    setScanningUiState(type, context, false)
  }
}

const capturePhotoFromCamera = () =>
  new Promise((resolve, reject) => {
    if (!(navigator?.mediaDevices?.getUserMedia)) {
      reject(new Error('MEDIA_DEVICES_UNAVAILABLE'))
      return
    }

    const applyStyles = (el, styles) => {
      Object.assign(el.style, styles)
    }

    const overlay = document.createElement('div')
    applyStyles(overlay, {
      position: 'fixed',
      inset: '0',
      backgroundColor: 'rgba(0,0,0,0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      zIndex: '9998',
    })

    const panel = document.createElement('div')
    applyStyles(panel, {
      width: '100%',
      maxWidth: '420px',
      backgroundColor: '#111827',
      color: '#fff',
      borderRadius: '24px',
      padding: '20px',
      boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    })

    const closeBtn = document.createElement('button')
    closeBtn.type = 'button'
    closeBtn.textContent = '×'
    applyStyles(closeBtn, {
      position: 'absolute',
      top: '12px',
      right: '16px',
      background: 'transparent',
      border: 'none',
      color: '#fff',
      fontSize: '24px',
      cursor: 'pointer',
    })
    panel.appendChild(closeBtn)

    const title = document.createElement('div')
    title.textContent = 'Kamera – bitte Fahrzeugschein ausrichten'
    applyStyles(title, { fontWeight: '600', fontSize: '16px' })
    panel.appendChild(title)

    const videoWrapper = document.createElement('div')
    applyStyles(videoWrapper, {
      width: '100%',
      backgroundColor: '#000',
      borderRadius: '16px',
      overflow: 'hidden',
      display: 'flex',
      justifyContent: 'center',
      position: 'relative',
      minHeight: '260px',
    })

    const video = document.createElement('video')
    video.autoplay = true
    video.playsInline = true
    video.muted = true
    applyStyles(video, { width: '100%' })
    videoWrapper.appendChild(video)

    const previewImg = document.createElement('img')
    previewImg.alt = 'Aufnahme Vorschau'
    applyStyles(previewImg, { display: 'none', width: '100%', objectFit: 'cover' })
    videoWrapper.appendChild(previewImg)

    panel.appendChild(videoWrapper)

    const controls = document.createElement('div')
    applyStyles(controls, {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
    })

    const captureBtn = document.createElement('button')
    captureBtn.type = 'button'
    captureBtn.textContent = 'Foto aufnehmen'
    applyStyles(captureBtn, {
      flex: '1',
      padding: '12px',
      borderRadius: '14px',
      border: 'none',
      backgroundColor: '#10b981',
      color: '#fff',
      fontWeight: '600',
      cursor: 'pointer',
    })

    const acceptBtn = document.createElement('button')
    acceptBtn.type = 'button'
    acceptBtn.textContent = 'Verwenden'
    acceptBtn.disabled = true
    applyStyles(acceptBtn, {
      flex: '1',
      padding: '12px',
      borderRadius: '14px',
      border: 'none',
      backgroundColor: '#3b82f6',
      color: '#fff',
      fontWeight: '600',
      cursor: 'pointer',
      opacity: '0.6',
    })

    const retakeBtn = document.createElement('button')
    retakeBtn.type = 'button'
    retakeBtn.textContent = 'Neu aufnehmen'
    retakeBtn.disabled = true
    applyStyles(retakeBtn, {
      flexBasis: '100%',
      padding: '10px',
      borderRadius: '12px',
      border: '1px solid rgba(255,255,255,0.3)',
      background: 'transparent',
      color: '#fff',
      cursor: 'pointer',
      opacity: '0.6',
    })

    controls.appendChild(captureBtn)
    controls.appendChild(acceptBtn)
    controls.appendChild(retakeBtn)
    panel.appendChild(controls)
    overlay.appendChild(panel)
    document.body.appendChild(overlay)

    const canvas = document.createElement('canvas')
    let stream

    const setDisabledState = (btn, disabled) => {
      btn.disabled = disabled
      btn.style.opacity = disabled ? '0.6' : '1'
      btn.style.cursor = disabled ? 'not-allowed' : 'pointer'
    }

    const cleanup = (err) => {
      stream?.getTracks()?.forEach((track) => track.stop())
      overlay.remove()
      if (err) {
        reject(err)
      }
    }

    const startStream = async () => {
      try {
        stream?.getTracks()?.forEach((track) => track.stop())
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        })
        video.srcObject = stream
        await video.play()
      } catch (err) {
        cleanup(err)
      }
    }

    const handleCapture = () => {
      if (!video.videoWidth || !video.videoHeight) return
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.drawImage(video, 0, 0)
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9)
      previewImg.src = dataUrl
      previewImg.style.display = 'block'
      video.style.display = 'none'
      setDisabledState(acceptBtn, false)
      setDisabledState(retakeBtn, false)
      stream?.getTracks()?.forEach((track) => track.stop())
    }

    const handleRetake = () => {
      previewImg.style.display = 'none'
      video.style.display = 'block'
      setDisabledState(acceptBtn, true)
      setDisabledState(retakeBtn, true)
      startStream()
    }

    const handleAccept = () => {
      canvas.toBlob((blob) => {
        if (!blob) {
          cleanup(new Error('BLOB_ERROR'))
          return
        }
        const file = new File([blob], `scan-${Date.now()}.jpg`, { type: 'image/jpeg' })
        cleanup()
        resolve(file)
      }, 'image/jpeg', 0.92)
    }

    closeBtn.addEventListener('click', () => {
      cleanup(new Error('USER_ABORT'))
    })
    captureBtn.addEventListener('click', handleCapture)
    retakeBtn.addEventListener('click', handleRetake)
    acceptBtn.addEventListener('click', handleAccept)

    startStream()
  })

const startCameraScan = async (type, context) => {
  try {
    const file = await capturePhotoFromCamera()
    if (file) {
      await scanDocumentFile(file, type, context, 'camera')
    }
  } catch (err) {
    if (err?.message === 'USER_ABORT') {
      showToast('Kamera geschlossen', 'info')
    } else if (err?.message === 'MEDIA_DEVICES_UNAVAILABLE') {
      showToast('Kamera wird nicht unterstützt – bitte Foto hochladen', 'error')
    } else {
      showToast('Kamera konnte nicht gestartet werden', 'error')
      console.error('Camera scan error', err)
    }
  }
}

const normalizeVinToken = (value) =>
  value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .replace(/[IO]/g, '1')
    .replace(/Q/g, '0')

const extractFin = (text) => {
  if (!text) return ''
  const normalized = text.replace(/[|]/g, '1')
  const lines = normalized.split(/\r?\n/)
  const prioritizedTokens = []
  lines.forEach((line, index) => {
    const trimmed = line.trim().toUpperCase()
    if (!trimmed) return
    if (/(FIN|FAHRZEUG|E\b)/.test(trimmed)) {
      trimmed
        .split(/[\s:;-]+/)
        .filter(Boolean)
        .forEach((token) => prioritizedTokens.push(token))
      const nextLine = lines[index + 1]?.trim()
      if (nextLine) {
        nextLine
          .split(/[\s:;-]+/)
          .filter(Boolean)
          .forEach((token) => prioritizedTokens.push(token))
      }
    }
  })
  const generalMatches = normalized.match(/\b[A-HJ-NPR-Z0-9]{11,17}\b/gi) || []
  const candidates = [...prioritizedTokens, ...generalMatches]
  for (const token of candidates) {
    const vin = normalizeVinToken(token)
    if (vin.length >= 11 && vin.length <= 17) {
      return vin
    }
  }
  return ''
}

const extractHsnTsn = (text) => {
  if (!text) return { hsn: '', tsn: '' }
  const normalized = text.replace(/[\s]+/g, ' ')
  const lines = normalized.split(/\r?\n/)
  const findNearLabel = (regex, valueRegex) => {
    for (let i = 0; i < lines.length; i += 1) {
      if (regex.test(lines[i])) {
        const merged = `${lines[i]} ${lines[i + 1] ?? ''}`
        const match = merged.match(valueRegex)
        if (match?.[0]) return match[0]
      }
    }
    return null
  }

  const rawHsn =
    findNearLabel(/(2\.1|HSN)/i, /\b\d{4}\b/) || (normalized.match(/\b\d{4}\b/g) || [])[0]
  const rawTsn =
    findNearLabel(/(2\.2|TSN)/i, /\b[A-Z0-9]{3}\b/i) || (normalized.match(/\b[A-Z0-9]{3}\b/gi) || [])[0]

  const hsn = rawHsn ? sanitizeNumeric(rawHsn) : ''
  const tsn = rawTsn ? sanitizeTsn(rawTsn) : ''

  return { hsn: hsn || '', tsn: tsn || '' }
}

const syncHeroHeight = () => {
  const hero = document.getElementById('desktop-hero')
  if (!hero) return
  const formCol = document.getElementById('hero-form-col')
  const frameCol = document.getElementById('topFrameContainer')
  const heroRow = document.getElementById('hero-row')
  const contentHeight = Math.max(
    formCol?.scrollHeight || 0,
    frameCol?.scrollHeight || 0,
    heroRow?.scrollHeight || 0
  )
  const desired = Math.max(window.innerHeight * 0.85, contentHeight + 80)
  hero.style.minHeight = `${desired}px`
}

export const setupTabs = () => {
  if (typeof window === 'undefined') return () => {}

  const brandLogoOverrides = {
    vw: 'volkswagen',
    citroen: 'citron',
    mercedesbenz: 'mercedes-benz',
  }

  const normalizeBrandClass = (label) =>
    String(label || '')
      .toLowerCase()
      .replace(' ', '-')
      .replace(/[^a-z-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')

  const resolveBrandLogoClass = (label) => {
    const normalized = normalizeBrandClass(label)
    if (!normalized) return ''
    const override = brandLogoOverrides[normalized] || normalized
    return `${override}-logo`
  }

  const inquiryFormMap = {
    model: {
      desktop: {
        manufacturer: 'desktop-brand-select',
        maintype: 'desktop-model-select',
        firstRegYear: 'desktop-year-select',
        bodyDesigns: 'desktop-body-select',
        fuelTypes: 'desktop-fuel-select',
        gearboxTypes: 'desktop-gear-select',
        enginePower: 'desktop-power-select',
        subtypes: 'desktop-variant-select',
        mileage: 'desktop-mileage-select',
        email: 'desktop-model-email',
        phoneCode: 'desktop-model-phone-code',
        phonePrefix: 'desktop-model-phone-prefix',
        phoneNumber: 'desktop-model-phone-number',
      },
      mobile: {
        manufacturer: 'mobile-brand-select',
        maintype: 'mobile-model-select',
        firstRegYear: 'mobile-year-select',
        bodyDesigns: 'mobile-body-select',
        fuelTypes: 'mobile-fuel-select',
        gearboxTypes: 'mobile-gear-select',
        enginePower: 'mobile-power-select',
        subtypes: 'mobile-variant-select',
        mileage: 'mobile-mileage-select',
        email: 'mobile-model-email',
        phoneCode: 'mobile-model-phone-code',
        phonePrefix: 'mobile-model-phone-prefix',
        phoneNumber: 'mobile-model-phone-number',
      },
    },
    fin: {
      desktop: {
        vin: 'desktop-fin-input',
        firstRegYear: 'desktop-fin-year',
        mileage: 'desktop-fin-mileage',
        email: 'desktop-fin-email',
        phoneCode: 'desktop-fin-phone-code',
        phonePrefix: 'desktop-fin-phone-prefix',
        phoneNumber: 'desktop-fin-phone-number',
      },
      mobile: {
        vin: 'mobile-fin-input',
        firstRegYear: 'fin-year',
        mileage: 'fin-mileage',
        email: 'fin-email',
        phoneCode: 'fin-phone-code',
        phonePrefix: 'fin-phone-prefix',
        phoneNumber: 'fin-phone-number',
      },
    },
    hsn: {
      desktop: {
        hsn: 'desktop-hsn',
        tsn: 'desktop-tsn',
        firstRegYear: 'desktop-hsn-year',
        mileage: 'desktop-hsn-mileage',
        email: 'desktop-hsn-email',
        phoneCode: 'desktop-hsn-phone-code',
        phonePrefix: 'desktop-hsn-phone-prefix',
        phoneNumber: 'desktop-hsn-phone-number',
      },
      mobile: {
        hsn: 'mobile-hsn',
        tsn: 'mobile-tsn',
        firstRegYear: 'hsn-year',
        mileage: 'hsn-mileage',
        email: 'hsn-email',
        phoneCode: 'hsn-phone-code',
        phonePrefix: 'hsn-phone-prefix',
        phoneNumber: 'hsn-phone-number',
      },
    },
  }

  const readInputValue = (id) => {
    const el = document.getElementById(id)
    if (!el) return ''
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      return el.value?.trim() || ''
    }
    if (el instanceof HTMLSelectElement) {
      return el.value?.trim() || ''
    }
    return ''
  }

  const readSelectOption = (id) => {
    const el = document.getElementById(id)
    if (!(el instanceof HTMLSelectElement)) {
      return { value: '', label: '' }
    }
    const option = el.options[el.selectedIndex]
    return {
      value: option?.value?.trim() || '',
      label: option?.textContent?.trim() || '',
    }
  }

  const readCheckbox = (container) => {
    if (!container) return false
    const checkbox =
      container.querySelector('.agb-consent input[type="checkbox"]') ||
      container.querySelector('input[type="checkbox"]')
    return Boolean(checkbox && checkbox.checked)
  }

  const normalizeDigits = (value) => String(value || '').replace(/\D/g, '')

  const buildPhoneNumber = (code, prefix, number) => {
    const cleanedCode = String(code || '').trim()
    const cleanedPrefix = normalizeDigits(prefix)
    const cleanedNumber = normalizeDigits(number)
    if (!cleanedCode && !cleanedPrefix && !cleanedNumber) return ''
    return [cleanedCode, cleanedPrefix, cleanedNumber].filter(Boolean).join(' ')
  }

  const normalizeMileage = (value) => {
    const trimmed = String(value || '').trim()
    const numeric = normalizeDigits(trimmed)
    if (!numeric) return trimmed
    return Number(numeric)
  }

  const buildInquiryDraft = (group, flow, container) => {
    const mapping = inquiryFormMap?.[flow]?.[group]
    if (!mapping) return null

    const email = readInputValue(mapping.email)
    const phoneNumber = buildPhoneNumber(
      readInputValue(mapping.phoneCode),
      readInputValue(mapping.phonePrefix),
      readInputValue(mapping.phoneNumber)
    )
    const termsAccepted = readCheckbox(container)
    const firstRegYear = readSelectOption(mapping.firstRegYear)
    const mileage = normalizeMileage(readInputValue(mapping.mileage))

    const inquiryBase = {
      first_reg_year: firstRegYear.value || null,
      mileage: mileage || null,
      email: email || null,
      phone_number: phoneNumber || null,
      terms_of_service: termsAccepted,
      newsletter: false,
      dat_engine: null,
      dat_propulsion: null,
    }

    if (flow === 'model') {
      const manufacturer = readSelectOption(mapping.manufacturer)
      const maintype = readSelectOption(mapping.maintype)
      const bodyDesigns = readSelectOption(mapping.bodyDesigns)
      const fuelTypes = readSelectOption(mapping.fuelTypes)
      const gearboxTypes = readSelectOption(mapping.gearboxTypes)
      const enginePower = readSelectOption(mapping.enginePower)
      const subtypes = readSelectOption(mapping.subtypes)

      return {
        savedAt: new Date().toISOString(),
        flow,
        inquiry: {
          ...inquiryBase,
          make: manufacturer.label || null,
          model: maintype.label || null,
          subtype: subtypes.label || null,
          engine_power: enginePower.value || enginePower.label || null,
          body_design: bodyDesigns.label || null,
          gearbox_type: gearboxTypes.label || null,
          fuel_type: fuelTypes.label || null,
          dat_manufacturer_id: manufacturer.value || null,
          dat_maintype_id: maintype.value || null,
          dat_subtype_id: subtypes.value || null,
          dat_body: bodyDesigns.value || null,
          dat_gearbox: gearboxTypes.value || null,
        },
      }
    }

    if (flow === 'fin') {
      const vin = readInputValue(mapping.vin)
      return {
        savedAt: new Date().toISOString(),
        flow,
        inquiry: {
          ...inquiryBase,
          vehicle_identification_number: vin || null,
        },
      }
    }

    const hsn = readInputValue(mapping.hsn)
    const tsn = readInputValue(mapping.tsn)
    return {
      savedAt: new Date().toISOString(),
      flow,
      inquiry: {
        ...inquiryBase,
        hsn: hsn || null,
        tsn: tsn || null,
      },
    }
  }

  const isValidEmail = (value) =>
    /^[^\s@]+@([^\s@.,]+\.)+[^\s@.,]{2,}$/.test(String(value || '').trim())

  const setupInquirySubmission = () => {
    const submitButtons = Array.from(
      document.querySelectorAll('[data-submit-inquiry="true"]')
    )
    if (submitButtons.length === 0) return () => {}

    const handler = async (event) => {
      event.preventDefault()
      const button = event.currentTarget
      if (button?.getAttribute?.('data-submitting') === 'true') return
      button?.setAttribute?.('data-submitting', 'true')

      const container = button?.closest?.('[data-tab-content-group]')
      const group = container?.getAttribute?.('data-tab-content-group') || 'mobile'
      const flow = container?.getAttribute?.('data-tab-target') || 'model'
      const draft = buildInquiryDraft(group, flow, container)
      const email = draft?.inquiry?.email ? String(draft.inquiry.email) : ''

      if (!draft || !email) {
        showToast('Bitte E-Mail-Adresse eingeben.', 'error')
        button?.removeAttribute?.('data-submitting')
        window.location.assign('/email-confirm')
        return
      }

      if (!isValidEmail(email)) {
        showToast('E-Mail-Adresse ist fehlerhaft.', 'error')
        button?.removeAttribute?.('data-submitting')
        return
      }

      saveInquiryDraft(draft)

      try {
        const res = await submitInquiryDraft(draft)
        if (!res.ok && !res.redirected) {
          throw new Error(`Request failed with status ${res.status}`)
        }
      } catch (err) {
        console.warn('Failed to submit inquiry', err)
        showToast('Konnte die Anfrage nicht senden.', 'error')
      } finally {
        button?.removeAttribute?.('data-submitting')
      }

      const emailParam = encodeURIComponent(email)
      window.location.assign(`/email-confirm?email=${emailParam}`)
    }

    submitButtons.forEach((btn) => btn.addEventListener('click', handler))
    return () => {
      submitButtons.forEach((btn) => btn.removeEventListener('click', handler))
    }
  }

  const dropdownControllers = new Map()

  const modelStepMap = {
    mobile: [
      'mobile-model-step-1',
      'mobile-model-step-2',
      'mobile-model-step-3',
      'mobile-model-step-body',
      'mobile-model-step-4',
      'mobile-model-step-5',
      'mobile-model-step-6',
      'mobile-model-step-7',
      'mobile-model-step-8',
      'mobile-model-step-9',
    ],
    desktop: [
      'desktop-model-step-1',
      'desktop-model-step-2',
      'desktop-model-step-3',
      'desktop-model-step-body',
      'desktop-model-step-4',
      'desktop-model-step-5',
      'desktop-model-step-6',
      'desktop-model-step-7',
      'desktop-model-step-8',
      'desktop-model-step-9',
    ],
  }

  const modelProgressBars = {
    mobile: 'mobile-model-progress',
    desktop: 'desktop-model-progress',
  }

  const updateModelProgress = (view) => {
    const steps = modelStepMap[view]
    const barId = modelProgressBars[view]
    if (!steps || !barId) return
    const bar = document.getElementById(barId)
    if (!bar) return
    const activeCount = steps.reduce((count, id) => {
      const el = document.getElementById(id)
      return count + (el?.classList.contains('active') ? 1 : 0)
    }, 0)
    const total = steps.length || 1
    const pct = Math.max(6, Math.min(100, Math.round((activeCount / total) * 100)))
    bar.style.width = `${pct}%`
  }

  const syncModelProgressForStep = (stepId) => {
    const entry = Object.entries(modelStepMap).find(([, steps]) => steps.includes(stepId))
    if (entry) {
      updateModelProgress(entry[0])
    }
  }

  const createDropdownStructure = (
    selectEl,
    { placeholderText = '', includeLogo = false } = {}
  ) => {
    if (!selectEl) return null
    if (dropdownControllers.has(selectEl.id)) {
      return dropdownControllers.get(selectEl.id)
    }

    const wrapper = document.createElement('div')
    wrapper.className = 'brand-dropdown'
    if (!includeLogo) wrapper.classList.add('brand-dropdown--text')
    const parent = selectEl.parentNode
    parent.insertBefore(wrapper, selectEl)
    wrapper.appendChild(selectEl)
    const formStep = wrapper.closest('.form-step')

    selectEl.classList.add('brand-native-select')
    selectEl.setAttribute('aria-hidden', 'true')
    selectEl.tabIndex = -1

    const trigger = document.createElement('button')
    trigger.type = 'button'
    trigger.className = 'brand-trigger'
    if (!includeLogo) trigger.classList.add('brand-trigger--text-only')
    trigger.setAttribute('aria-haspopup', 'listbox')
    trigger.setAttribute('aria-expanded', 'false')

    const triggerLabel = document.createElement('span')
    triggerLabel.className = 'brand-trigger-label'
    triggerLabel.textContent = placeholderText
    trigger.appendChild(triggerLabel)

    const triggerCaret = document.createElement('span')
    triggerCaret.className = 'brand-trigger-caret'
    triggerCaret.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 9l6 6 6-6"></path></svg>'
    trigger.appendChild(triggerCaret)

    wrapper.appendChild(trigger)

    const optionsPanel = document.createElement('div')
    optionsPanel.className = 'brand-options'
    optionsPanel.setAttribute('role', 'listbox')
    wrapper.appendChild(optionsPanel)

    const toggleOptions = (force) => {
      if (selectEl.disabled) return
      const willOpen =
        typeof force === 'boolean' ? force : !wrapper.classList.contains('brand-dropdown-open')
      wrapper.classList.toggle('brand-dropdown-open', willOpen)
      if (formStep) {
        formStep.classList.toggle('form-step--dropdown-open', willOpen)
      }
      trigger.setAttribute('aria-expanded', String(willOpen))
    }

    const syncDisabled = () => {
      const isDisabled = Boolean(selectEl.disabled)
      trigger.disabled = isDisabled
      wrapper.classList.toggle('brand-dropdown--disabled', isDisabled)
      if (isDisabled) {
        wrapper.classList.remove('brand-dropdown-open')
        if (formStep) {
          formStep.classList.remove('form-step--dropdown-open')
        }
        trigger.setAttribute('aria-expanded', 'false')
      }
    }

    const updateTrigger = (item) => {
      if (item && item.label) {
        triggerLabel.textContent = item.label
      } else {
        triggerLabel.textContent = placeholderText
      }
    }

    const setOptions = (items = []) => {
      optionsPanel.innerHTML = ''
      items.forEach((item) => {
        const optionBtn = document.createElement('button')
        optionBtn.type = 'button'
        optionBtn.className = 'brand-option'
        if (!includeLogo) optionBtn.classList.add('brand-option--text')

        if (includeLogo) {
          if (item.logoClass) {
            const optionLogo = document.createElement('span')
            optionLogo.className = `brand-logo ${item.logoClass}`
            optionLogo.setAttribute('aria-hidden', 'true')
            optionBtn.appendChild(optionLogo)
          } else if (item.logo) {
            const optionLogo = document.createElement('img')
            optionLogo.src = item.logo
            optionLogo.alt = `${item.label} Logo`
            optionBtn.appendChild(optionLogo)
          }
        }

        const optionLabel = document.createElement('span')
        optionLabel.textContent = item.label ?? item.value ?? ''
        optionBtn.appendChild(optionLabel)

        optionBtn.addEventListener('click', () => {
          selectEl.value = item.value ?? ''
          selectEl.dispatchEvent(new Event('change', { bubbles: true }))
          updateTrigger(item)
          toggleOptions(false)
        })

        optionsPanel.appendChild(optionBtn)
      })
    }

    trigger.addEventListener('click', () => {
      if (selectEl.disabled) return
      toggleOptions()
    })

    const outsideHandler = (event) => {
      if (!wrapper.contains(event.target)) {
        toggleOptions(false)
      }
    }
    document.addEventListener('click', outsideHandler)

    const escHandler = (event) => {
      if (event.key === 'Escape') {
        toggleOptions(false)
      }
    }
    document.addEventListener('keydown', escHandler)

    const controller = {
      setOptions,
      updateSelection: updateTrigger,
      syncDisabled,
      cleanup: () => {
        document.removeEventListener('click', outsideHandler)
        document.removeEventListener('keydown', escHandler)
        if (formStep) {
          formStep.classList.remove('form-step--dropdown-open')
        }
        wrapper.replaceWith(selectEl)
        selectEl.classList.remove('brand-native-select')
        selectEl.removeAttribute('aria-hidden')
        selectEl.tabIndex = 0
        dropdownControllers.delete(selectEl.id)
      },
    }

    dropdownControllers.set(selectEl.id, controller)
    syncDisabled()
    return controller
  }

  const enhanceBrandSelect = (selectEl, placeholderText = 'Marke auswaehlen') => {
    if (!selectEl) return null
    const resolvedPlaceholder =
      placeholderText || selectEl.options[0]?.textContent?.trim() || 'Marke auswaehlen'
    const controller = createDropdownStructure(selectEl, {
      placeholderText: resolvedPlaceholder,
      includeLogo: true,
    })
    if (!controller) return null

    const syncFromSelect = () => {
      const items = Array.from(selectEl.options || [])
        .filter((opt) => opt.value)
          .map((opt) => ({
            value: opt.value,
            label: opt.textContent?.trim() || opt.value,
            logo: opt.dataset?.logo,
            logoClass: opt.dataset?.logoClass,
          }))
      controller.setOptions(items)
      const selectedOption = selectEl.options[selectEl.selectedIndex]
      if (selectedOption && selectedOption.value) {
          controller.updateSelection({
            value: selectedOption.value,
            label: selectedOption.textContent?.trim() || selectedOption.value,
            logo: selectedOption.dataset?.logo,
            logoClass: selectedOption.dataset?.logoClass,
          })
      } else {
        controller.updateSelection(null)
      }
    }

    controller.syncFromSelect = syncFromSelect
    syncFromSelect()
    controller.syncDisabled?.()
    return controller
  }

  const enhanceSimpleSelect = (selectEl) => {
    if (!selectEl) return null
    if (!(selectEl instanceof HTMLSelectElement)) return null
    const placeholderText = selectEl.options[0]?.textContent?.trim() || ''
    const controller = createDropdownStructure(selectEl, {
      placeholderText,
      includeLogo: false,
    })
    if (!controller) return null

    const syncFromSelect = () => {
      const items = Array.from(selectEl.options || [])
        .filter((opt) => opt.value)
        .map((opt) => ({
          value: opt.value,
          label: opt.textContent?.trim() || opt.value,
        }))
      controller.setOptions(items)
      const selectedOption = selectEl.options[selectEl.selectedIndex]
      if (selectedOption && selectedOption.value) {
        controller.updateSelection({
          value: selectedOption.value,
          label: selectedOption.textContent?.trim() || selectedOption.value,
        })
      } else {
        controller.updateSelection(null)
      }
    }

    controller.syncFromSelect = syncFromSelect
    syncFromSelect()
    controller.syncDisabled?.()
    return controller
  }

  
  const enhanceComboInput = (inputEl) => {
    if (!inputEl) return null
    if (dropdownControllers.has(inputEl.id)) {
      return dropdownControllers.get(inputEl.id)
    }

    const wrapper = document.createElement('div')
    wrapper.className = 'brand-dropdown brand-dropdown--combo'
    const parent = inputEl.parentNode
    parent.insertBefore(wrapper, inputEl)
    wrapper.appendChild(inputEl)
    const formStep = wrapper.closest('.form-step')

    const optionsPanel = document.createElement('div')
    optionsPanel.className = 'brand-options'
    optionsPanel.setAttribute('role', 'listbox')
    wrapper.appendChild(optionsPanel)

    let optionItems = []

    const setOptions = (items = []) => {
      optionItems = Array.isArray(items) ? items : []
      renderOptions(optionItems)
    }

    const renderOptions = (items) => {
      optionsPanel.innerHTML = ''
      items.forEach((item) => {
        const optionBtn = document.createElement('button')
        optionBtn.type = 'button'
        optionBtn.className = 'brand-option brand-option--text'
        const optionLabel = document.createElement('span')
        optionLabel.textContent = item.label ?? item.value ?? ''
        optionBtn.appendChild(optionLabel)
        optionBtn.addEventListener('click', () => {
          inputEl.value = item.value ?? item.label ?? ''
          inputEl.dispatchEvent(new Event('input', { bubbles: true }))
          toggleOptions(false)
        })
        optionsPanel.appendChild(optionBtn)
      })
    }

    const toggleOptions = (force) => {
      if (inputEl.disabled) return
      const willOpen =
        typeof force === 'boolean' ? force : !wrapper.classList.contains('brand-dropdown-open')
      wrapper.classList.toggle('brand-dropdown-open', willOpen)
      if (formStep) {
        formStep.classList.toggle('form-step--dropdown-open', willOpen)
      }
    }

    const syncDisabled = () => {
      const isDisabled = Boolean(inputEl.disabled)
      wrapper.classList.toggle('brand-dropdown--disabled', isDisabled)
      if (isDisabled) {
        wrapper.classList.remove('brand-dropdown-open')
        if (formStep) {
          formStep.classList.remove('form-step--dropdown-open')
        }
      }
    }

    const filterOptions = () => {
      const query = String(inputEl.value || '').toLowerCase()
      if (!query) {
        renderOptions(optionItems)
        return
      }
      const filtered = optionItems.filter((item) =>
        String(item.label ?? item.value ?? '').toLowerCase().includes(query)
      )
      renderOptions(filtered)
    }

    const focusHandler = () => {
      if (inputEl.disabled) return
      toggleOptions(true)
    }

    const inputHandler = () => {
      if (inputEl.disabled) return
      toggleOptions(true)
      filterOptions()
    }

    inputEl.addEventListener('focus', focusHandler)
    inputEl.addEventListener('click', focusHandler)
    inputEl.addEventListener('input', inputHandler)

    const outsideHandler = (event) => {
      if (!wrapper.contains(event.target)) {
        toggleOptions(false)
      }
    }
    document.addEventListener('click', outsideHandler)

    const escHandler = (event) => {
      if (event.key === 'Escape') {
        toggleOptions(false)
      }
    }
    document.addEventListener('keydown', escHandler)

    const controller = {
      setOptions,
      syncDisabled,
      cleanup: () => {
        inputEl.removeEventListener('focus', focusHandler)
        inputEl.removeEventListener('click', focusHandler)
        inputEl.removeEventListener('input', inputHandler)
        document.removeEventListener('click', outsideHandler)
        document.removeEventListener('keydown', escHandler)
        if (formStep) {
          formStep.classList.remove('form-step--dropdown-open')
        }
        wrapper.replaceWith(inputEl)
        dropdownControllers.delete(inputEl.id)
      },
    }

    dropdownControllers.set(inputEl.id, controller)
    syncDisabled()
    return controller
  }

const tabGroups = {
    mobile: { containerId: 'tab-content-container' },
    desktop: { containerId: 'desktop-tab-content-window' },
  }

  const lockContainerHeight = (containerId) => {
    const container = document.getElementById(containerId)
    if (!container) return null
    container.style.height = `${container.offsetHeight}px`
    return container
  }

  const expandContainerHeight = (containerId) => {
    const container = document.getElementById(containerId)
    if (!container) return
    requestAnimationFrame(() => {
      container.style.height = `${container.scrollHeight}px`
      setTimeout(() => {
        container.style.height = 'auto'
      }, 350)
    })
  }

  const scrollStepIntoView = (element) => {
    if (!element || typeof window === 'undefined') return
    const rect = element.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight
    const alreadyInView =
      rect.top >= 0 && rect.bottom <= viewportHeight && rect.top < viewportHeight * 0.8
    if (alreadyInView) return

    const offset = 64
    const startY = window.pageYOffset
    const rawTargetY = Math.max(0, rect.top + startY - offset)
    // Cap how far we move per activation to avoid large jumps on long forms (mobile)
    const cappedTargetY = startY + Math.max(-260, Math.min(260, rawTargetY - startY))
    const distance = cappedTargetY - startY
    if (Math.abs(distance) < 8) return

    const duration = Math.min(520, Math.max(320, Math.abs(distance) * 0.85))
    const easeInOut = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t)
    let startTime = null

    const step = (timestamp) => {
      if (startTime === null) startTime = timestamp
      const elapsed = timestamp - startTime
      const progress = Math.min(1, elapsed / duration)
      const eased = easeInOut(progress)
      const nextY = startY + distance * eased
      window.scrollTo(0, nextY)
      if (progress < 1) {
        requestAnimationFrame(step)
      }
    }

    requestAnimationFrame(step)
  }

  const showNextStep = (stepId, containerId = 'tab-content-container', options = {}) => {
    const resolvedOptions =
      options && typeof options === 'object' ? options : { scroll: Boolean(options) }
    const { scroll = true } = resolvedOptions
    const stepElement = document.getElementById(stepId)
    let activated = false
    if (stepElement && !stepElement.classList.contains('active')) {
      stepElement.classList.add('active')
      activated = true
      const container = document.getElementById(containerId)
      if (container) {
        container.style.height = 'auto'
      }
      syncHeroHeight()
      if (scroll) {
        requestAnimationFrame(() => scrollStepIntoView(stepElement))
      }
    }
    syncModelProgressForStep(stepId)
  }

  const checkEmailAndShowNext = (
    emailInputId,
    nextStepId,
    containerId = 'tab-content-container'
  ) => {
    const emailInput = document.getElementById(emailInputId)
    if (emailInput && emailInput.value.length > 3 && emailInput.value.includes('@')) {
      showNextStep(nextStepId, containerId)
    }
  }

  const switchTab = (group, tabName) => {
    const config = tabGroups[group]
    if (!config) return
    const container = document.getElementById(config.containerId)

    document
      .querySelectorAll(`[data-tab-group="${group}"]`)
      .forEach((btn) => btn.classList.remove('active'))
    document
      .querySelectorAll(`[data-tab-content-group="${group}"]`)
      .forEach((section) => section.classList.add('hidden'))

    const targetBtn = document.querySelector(
      `[data-tab-group="${group}"][data-tab-target="${tabName}"]`
    )
    if (targetBtn) {
      targetBtn.classList.add('active')
    }

    const targetContent = document.querySelector(
      `[data-tab-content-group="${group}"][data-tab-target="${tabName}"]`
    )
    if (targetContent) {
      targetContent.classList.remove('hidden')
      scrollStepIntoView(targetContent)
    }

    if (container) {
      container.style.height = 'auto'
    }

    syncHeroHeight()
  }

  const setMobileRadius = (tabName) => {
    const container = document.getElementById('tab-content-container')
    if (!container) return
    container.classList.remove(
      'mobile-tab-content-window--fin',
      'mobile-tab-content-window--hsn',
      'mobile-tab-content-window--model'
    )
    if (tabName === 'fin') {
      container.classList.add('mobile-tab-content-window--fin')
    } else if (tabName === 'hsn') {
      container.classList.add('mobile-tab-content-window--hsn')
    } else {
      container.classList.add('mobile-tab-content-window--model')
    }
  }

  const switchMobileTab = (tabName) => {
    switchTab('mobile', tabName)
    setMobileRadius(tabName)
    const container = document.getElementById('tab-content-container')
    if (container) {
      container.classList.remove('rounded-tl-none', 'rounded-tr-none')
      container.classList.add('rounded-tl-3xl', 'rounded-tr-3xl')
    }
  }

  const setDesktopRadius = (tabName) => {
    const container = document.getElementById('desktop-tab-content-window')
    if (!container) return
    container.classList.remove(
      'desktop-tab-content-window--fin',
      'desktop-tab-content-window--hsn',
      'desktop-tab-content-window--model'
    )
    if (tabName === 'fin') {
      container.classList.add('desktop-tab-content-window--fin')
    } else if (tabName === 'hsn') {
      container.classList.add('desktop-tab-content-window--hsn')
    } else {
      container.classList.add('desktop-tab-content-window--model')
    }
  }

  const switchDesktopTab = (tabName) => {
    setDesktopRadius(tabName)
    switchTab('desktop', tabName)
  }

  const checkHsnTsn = () => {
    const hsn = document.getElementById('mobile-hsn')
    const tsn = document.getElementById('mobile-tsn')
    if (hsn?.value.length === 4 && tsn?.value.length === 3) {
      showNextStep('hsn-step-2')
    }
  }

  const checkHsnTsnDesktop = () => {
    const hsn = document.getElementById('desktop-hsn')
    const tsn = document.getElementById('desktop-tsn')
    if (hsn?.value.length === 4 && tsn?.value.length === 3) {
      showNextStep('desktop-hsn-step-1', 'desktop-tab-content-window')
    }
  }

  const checkFinInput = () => {
    const mobileInput = document.getElementById('mobile-fin-input')
    const desktopInput = document.getElementById('desktop-fin-input')

    if (mobileInput && mobileInput.value.length > 5) {
      showNextStep('fin-step-1')
    }

    if (desktopInput && desktopInput.value.length > 5) {
      showNextStep('desktop-fin-step-1', 'desktop-tab-content-window')
    }
  }

  const handleFileUpload = async (input, type, context) => {
    if (!(input.files && input.files[0])) return

    const labelSpan = input.parentElement.querySelector('span')
    const originalText = labelSpan?.textContent
    if (labelSpan) labelSpan.textContent = 'Scannen...'

    try {
      await scanDocumentFile(input.files[0], type, context, 'upload')
    } finally {
      if (labelSpan && originalText) labelSpan.textContent = originalText
      input.value = ''
    }
  }

  const PHONE_PREFIX_MIN = 2
  const PHONE_PREFIX_MAX = 4
  const PHONE_NUMBER_MIN = 6
  const PHONE_NUMBER_MAX = 8

  const normalizePhoneInput = (input, maxLen) => {
    if (!input) return ''
    const cleaned = String(input.value || '')
      .replace(/\D/g, '')
      .slice(0, maxLen)
    if (input.value !== cleaned) {
      input.value = cleaned
    }
    return cleaned
  }

  const checkPhoneAndShow = (prefixId, numberId, nextStepId, containerId) => {
    const prefix = document.getElementById(prefixId)
    const number = document.getElementById(numberId)
    if (!prefix || !number) return

    const prefixValue = normalizePhoneInput(prefix, PHONE_PREFIX_MAX)
    const numberValue = normalizePhoneInput(number, PHONE_NUMBER_MAX)

    if (
      prefixValue.length >= PHONE_PREFIX_MIN &&
      prefixValue.length <= PHONE_PREFIX_MAX &&
      numberValue.length >= PHONE_NUMBER_MIN &&
      numberValue.length <= PHONE_NUMBER_MAX
    ) {
      showNextStep(nextStepId, containerId)
    }
  }

  const setInitialHeights = () => {
    ;['tab-content-container', 'desktop-tab-content-window'].forEach((id) => {
      const container = document.getElementById(id)
      if (container) {
        container.style.height = 'auto'
      }
    })
    syncHeroHeight()
  }

  const loadHandler = () => setInitialHeights()
  window.addEventListener('load', loadHandler)
  setInitialHeights()
  window.addEventListener('resize', syncHeroHeight)

  // Observe hero/form size changes to keep height in sync
  let heroObserver
  const heroRow = document.getElementById('hero-row')
  if (typeof ResizeObserver !== 'undefined' && heroRow) {
    heroObserver = new ResizeObserver(() => syncHeroHeight())
    heroObserver.observe(heroRow)
  }

  // Hook up tab buttons (React drops inline onclicks)
  const cleanupFns = []
  document
    .querySelectorAll('[data-tab-group="mobile"]')
    .forEach((btn) => {
      const tabName = btn.getAttribute('data-tab-target')
      const handler = () => switchMobileTab(tabName)
      btn.addEventListener('click', handler)
      cleanupFns.push(() => btn.removeEventListener('click', handler))
    })

  document
    .querySelectorAll('[data-tab-group="desktop"]')
    .forEach((btn) => {
      const tabName = btn.getAttribute('data-tab-target')
      const handler = () => switchDesktopTab(tabName)
      btn.addEventListener('click', handler)
      cleanupFns.push(() => btn.removeEventListener('click', handler))
    })

  // Hook up inputs/selects that relied on inline attributes
  const wire = (selector, event, handler) => {
    const el = document.querySelector(selector)
    if (!el) return
    el.addEventListener(event, handler)
    cleanupFns.push(() => el.removeEventListener(event, handler))
  }

  // FIN desktop
  wire('#desktop-fin-year', 'change', () =>
    showNextStep('desktop-fin-step-2', 'desktop-tab-content-window')
  )
  wire('#desktop-fin-mileage', 'change', () =>
    showNextStep('desktop-fin-step-3', 'desktop-tab-content-window')
  )
  wire('#desktop-fin-email', 'input', () =>
    checkEmailAndShowNext('desktop-fin-email', 'desktop-fin-step-4', 'desktop-tab-content-window')
  )
  // desktop-fin-step-5 (button) removed, step 6 is now target
  wire('#desktop-fin-input', 'input', () => checkFinInput())
  wire('#desktop-fin-scan', 'click', () => startCameraScan('fin', 'desktop'))
  wire('#desktop-fin-upload', 'change', (e) => handleFileUpload(e.target, 'fin', 'desktop'))
  
  // Wire up Desktop FIN phone inputs
  wire('#desktop-fin-phone-code', 'change', () => checkPhoneAndShow('desktop-fin-phone-prefix', 'desktop-fin-phone-number', 'desktop-fin-step-6', 'desktop-tab-content-window'))
  wire('#desktop-fin-phone-prefix', 'input', () => checkPhoneAndShow('desktop-fin-phone-prefix', 'desktop-fin-phone-number', 'desktop-fin-step-6', 'desktop-tab-content-window'))
  wire('#desktop-fin-phone-number', 'input', () => checkPhoneAndShow('desktop-fin-phone-prefix', 'desktop-fin-phone-number', 'desktop-fin-step-6', 'desktop-tab-content-window'))

  // HSN/TSN desktop
  wire('#desktop-hsn', 'input', () => checkHsnTsnDesktop())
  wire('#desktop-tsn', 'input', () => checkHsnTsnDesktop())
  wire('#desktop-hsn-year', 'change', () =>
    showNextStep('desktop-hsn-step-2', 'desktop-tab-content-window')
  )
  wire('#desktop-hsn-mileage', 'change', () =>
    showNextStep('desktop-hsn-step-3', 'desktop-tab-content-window')
  )
  wire('#desktop-hsn-email', 'input', () =>
    checkEmailAndShowNext('desktop-hsn-email', 'desktop-hsn-step-4', 'desktop-tab-content-window')
  )
  // desktop-hsn-step-5 (button) removed, step 6 is now target
  wire('#desktop-hsn-upload', 'change', (e) => handleFileUpload(e.target, 'hsn', 'desktop'))
  wire('#desktop-hsn-scan', 'click', () => startCameraScan('hsn', 'desktop'))
  
  // Wire up Desktop HSN phone inputs
  wire('#desktop-hsn-phone-code', 'change', () => checkPhoneAndShow('desktop-hsn-phone-prefix', 'desktop-hsn-phone-number', 'desktop-hsn-step-6', 'desktop-tab-content-window'))
  wire('#desktop-hsn-phone-prefix', 'input', () => checkPhoneAndShow('desktop-hsn-phone-prefix', 'desktop-hsn-phone-number', 'desktop-hsn-step-6', 'desktop-tab-content-window'))
  wire('#desktop-hsn-phone-number', 'input', () => checkPhoneAndShow('desktop-hsn-phone-prefix', 'desktop-hsn-phone-number', 'desktop-hsn-step-6', 'desktop-tab-content-window'))


  // FIN mobile
  wire('#mobile-fin-input', 'input', () => checkFinInput())
  wire('#fin-year', 'change', () => showNextStep('fin-step-2'))
  wire('#fin-mileage', 'change', () => showNextStep('fin-step-3'))
  wire('#fin-email', 'input', () => checkEmailAndShowNext('fin-email', 'fin-step-4'))
  // checkPhone used here directly as mobile logic was simpler, keeping it consistent or could upgrade too
  // Original mobile logic was:
  // wire('#fin-phone-code', 'change', () => checkPhone('fin-step-5'))
  // Let's upgrade mobile to use checkPhoneAndShow too for better UX consistency if desired, 
  // but sticking to original behavior per request for desktop fix mostly.
  // Actually, let's keep mobile as is unless requested, to minimize regression risk.
  const checkPhoneMobile = (...args) => {
    if (args.length === 1) {
      showNextStep(args[0])
      return
    }
    checkPhoneAndShow(args[0], args[1], args[2], 'tab-content-container')
  }

  wire('#fin-phone-code', 'change', () =>
    checkPhoneMobile('fin-phone-prefix', 'fin-phone-number', 'fin-step-5')
  )
  wire('#fin-phone-prefix', 'input', () =>
    checkPhoneMobile('fin-phone-prefix', 'fin-phone-number', 'fin-step-5')
  )
  wire('#fin-phone-number', 'input', () =>
    checkPhoneMobile('fin-phone-prefix', 'fin-phone-number', 'fin-step-5')
  )
  wire('#mobile-fin-scan', 'click', () => startCameraScan('fin', 'mobile'))
  wire('#mobile-fin-upload', 'change', (e) => handleFileUpload(e.target, 'fin', 'mobile'))

  // HSN mobile
  wire('#mobile-hsn', 'input', () => checkHsnTsn())
  wire('#mobile-tsn', 'input', () => checkHsnTsn())
  wire('#hsn-year', 'change', () => showNextStep('hsn-step-3'))
  wire('#hsn-mileage', 'change', () => showNextStep('hsn-step-4'))
  wire('#hsn-email', 'input', () => checkEmailAndShowNext('hsn-email', 'hsn-step-5'))
  wire('#hsn-phone-code', 'change', () =>
    checkPhoneMobile('hsn-phone-prefix', 'hsn-phone-number', 'hsn-step-6')
  )
  wire('#hsn-phone-prefix', 'input', () =>
    checkPhoneMobile('hsn-phone-prefix', 'hsn-phone-number', 'hsn-step-6')
  )
  wire('#hsn-phone-number', 'input', () =>
    checkPhoneMobile('hsn-phone-prefix', 'hsn-phone-number', 'hsn-step-6')
  )
  wire('#mobile-hsn-upload', 'change', (e) => handleFileUpload(e.target, 'hsn', 'mobile'))
  wire('#mobile-hsn-scan', 'click', () => startCameraScan('hsn', 'mobile'))

  const brandCleanupFns = []
  const populateSelect = (selectEl, options, placeholder = null) => {
    if (!selectEl) return
    selectEl.innerHTML = ''
    if (placeholder) {
      const opt = document.createElement('option')
      opt.value = ''
      opt.textContent = placeholder
      selectEl.appendChild(opt)
    }
    options.forEach((value) => {
      const opt = document.createElement('option')
      const isObject = value && typeof value === 'object'
      const optionValue = isObject ? value.value ?? value.name ?? value.label : value
      const optionLabel = isObject ? value.label ?? value.name ?? value.value : value
      const optionLogo = isObject ? value.logo : null
      const optionLogoClass = isObject ? value.logoClass : null

      opt.value = optionValue ?? ''
      opt.textContent = optionLabel ?? ''

      if (optionLogoClass) {
        opt.dataset.logoClass = optionLogoClass
      }

      if (optionLogo) {
        opt.dataset.logo = optionLogo
        opt.style.backgroundImage = `url(${optionLogo})`
        opt.style.backgroundRepeat = 'no-repeat'
        opt.style.backgroundPosition = '12px center'
        opt.style.backgroundSize = '22px 22px'
        opt.style.paddingLeft = '3rem'
      }

      selectEl.appendChild(opt)
    })

    const controller = dropdownControllers.get(selectEl.id)
    controller?.syncFromSelect?.()
    controller?.syncDisabled?.()
  }

  const setupBrandModelFlow = () => {
    const placeholders = {
      manufacturer: 'Marke auswaehlen',
      maintype: 'Modell auswaehlen',
      firstRegYear: 'Erstzulassung',
      bodyDesigns: 'Karosserieform',
      fuelTypes: 'Kraftstoff',
      gearboxTypes: 'Schaltung',
      enginePower: 'Motorleistung',
      subtypes: 'Untermodell',
      mileage: 'Kilometer',
    }

    const fieldConfig = {
      manufacturer: {
        selectIds: {
          desktop: 'desktop-brand-select',
          mobile: 'mobile-brand-select',
        },
        stepIds: {
          desktop: 'desktop-model-step-1',
          mobile: 'mobile-model-step-1',
        },
        useLogo: true,
      },
      maintype: {
        selectIds: {
          desktop: 'desktop-model-select',
          mobile: 'mobile-model-select',
        },
        stepIds: {
          desktop: 'desktop-model-step-2',
          mobile: 'mobile-model-step-2',
        },
      },
      firstRegYear: {
        selectIds: {
          desktop: 'desktop-year-select',
          mobile: 'mobile-year-select',
        },
        stepIds: {
          desktop: 'desktop-model-step-3',
          mobile: 'mobile-model-step-3',
        },
      },
      bodyDesigns: {
        selectIds: {
          desktop: 'desktop-body-select',
          mobile: 'mobile-body-select',
        },
        stepIds: {
          desktop: 'desktop-model-step-body',
          mobile: 'mobile-model-step-body',
        },
      },
      fuelTypes: {
        selectIds: {
          desktop: 'desktop-fuel-select',
          mobile: 'mobile-fuel-select',
        },
        stepIds: {
          desktop: 'desktop-model-step-4',
          mobile: 'mobile-model-step-4',
        },
      },
      gearboxTypes: {
        selectIds: {
          desktop: 'desktop-gear-select',
          mobile: 'mobile-gear-select',
        },
        stepIds: {
          desktop: 'desktop-model-step-5',
          mobile: 'mobile-model-step-5',
        },
      },
      enginePower: {
        selectIds: {
          desktop: 'desktop-power-select',
          mobile: 'mobile-power-select',
        },
        stepIds: {
          desktop: 'desktop-model-step-6',
          mobile: 'mobile-model-step-6',
        },
      },
      subtypes: {
        selectIds: {
          desktop: 'desktop-variant-select',
          mobile: 'mobile-variant-select',
        },
        stepIds: {
          desktop: 'desktop-model-step-7',
          mobile: 'mobile-model-step-7',
        },
      },
      mileage: {
        selectIds: {
          desktop: 'desktop-mileage-select',
          mobile: 'mobile-mileage-select',
        },
        stepIds: {
          desktop: 'desktop-model-step-8',
          mobile: 'mobile-model-step-8',
        },
        staticOptions: true,
      },
    }

    const fieldOrder = [
      'manufacturer',
      'maintype',
      'firstRegYear',
      'bodyDesigns',
      'fuelTypes',
      'gearboxTypes',
      'enginePower',
      'subtypes',
      'mileage',
    ]

    const contactStepIds = {
      desktop: 'desktop-model-step-9',
      mobile: 'mobile-model-step-9',
    }

    const modelState = {
      manufacturer: '',
      maintype: '',
      firstRegYear: '',
      bodyDesigns: '',
      fuelTypes: '',
      gearboxTypes: '',
      enginePower: '',
      subtypes: '',
      mileage: '',
    }

    const alwaysVisibleFields = new Set(['maintype', 'firstRegYear'])

    const getSelectsForField = (key) => {
      const config = fieldConfig[key]
      if (!config) return []
      return [config.selectIds.desktop, config.selectIds.mobile]
        .map((id) => document.getElementById(id))
        .filter(Boolean)
    }

    const setFieldEnabled = (key, enabled) => {
      const config = fieldConfig[key]
      if (!config) return
      getSelectsForField(key).forEach((selectEl) => {
        selectEl.disabled = !enabled
        const controller = dropdownControllers.get(selectEl.id)
        controller?.syncDisabled?.()
      })
    }

    const setFieldValue = (key, value) => {
      modelState[key] = value || ''
      getSelectsForField(key).forEach((selectEl) => {
        selectEl.value = value || ''
        const controller = dropdownControllers.get(selectEl.id)
        controller?.syncFromSelect?.()
        controller?.syncDisabled?.()
      })
    }

    const clearField = (key) => {
      const config = fieldConfig[key]
      if (!config) return
      if (config.staticOptions) {
        setFieldValue(key, '')
        setFieldEnabled(key, false)
        return
      }
      getSelectsForField(key).forEach((selectEl) => {
        populateSelect(selectEl, [], placeholders[key])
      })
      modelState[key] = ''
      setFieldEnabled(key, false)
    }

    const mapChoices = (choices: DatChoice[] | undefined, includeLogo = false) =>
      (choices || []).map((choice) => {
        const label = choice?.label ?? choice?.value ?? ''
        const option = {
          value: String(choice?.value ?? ''),
          label: String(label),
        }
        if (includeLogo) {
          const logoClass = resolveBrandLogoClass(label)
          if (logoClass) {
            option.logoClass = logoClass
          }
        }
        return option
      })

    const setFieldOptions = (key, choices) => {
      const config = fieldConfig[key]
      if (!config) return
      const options = mapChoices(choices, Boolean(config.useLogo))
      getSelectsForField(key).forEach((selectEl) => {
        if (selectEl instanceof HTMLSelectElement) {
          populateSelect(selectEl, options, placeholders[key])
        }
      })
    }

    const setFieldLoading = (key, message = 'Lade...') => {
      const config = fieldConfig[key]
      if (!config || config.staticOptions) return
      getSelectsForField(key).forEach((selectEl) => {
        if (selectEl instanceof HTMLSelectElement) {
          populateSelect(selectEl, [], message)
        }
      })
      setFieldEnabled(key, false)
    }

    const isCurrent = (expected) =>
      Object.entries(expected).every(([key, value]) => modelState[key] === value)

    const deactivateStep = (stepId) => {
      const stepEl = document.getElementById(stepId)
      if (stepEl) {
        stepEl.classList.remove('active')
      }
    }

    const deactivateFieldStep = (key) => {
      const config = fieldConfig[key]
      if (!config) return
      if (alwaysVisibleFields.has(key)) return
      deactivateStep(config.stepIds.desktop)
      deactivateStep(config.stepIds.mobile)
    }

    const showStepForView = (stepId, view, shouldScroll = true) => {
      if (!stepId) return
      const containerId = view === 'desktop' ? 'desktop-tab-content-window' : 'tab-content-container'
      const stepEl = document.getElementById(stepId)
      const canScroll = Boolean(stepEl && (stepEl.offsetParent || stepEl.getClientRects().length))
      showNextStep(stepId, containerId, { scroll: shouldScroll && canScroll })
    }

    const showFieldStep = (key, shouldScroll = true) => {
      const config = fieldConfig[key]
      if (!config) return
      showStepForView(config.stepIds.desktop, 'desktop', shouldScroll)
      showStepForView(config.stepIds.mobile, 'mobile', shouldScroll)
    }

    const showContactStep = (shouldScroll = true) => {
      showStepForView(contactStepIds.desktop, 'desktop', shouldScroll)
      showStepForView(contactStepIds.mobile, 'mobile', shouldScroll)
    }

    const resetAfter = (key) => {
      const index = fieldOrder.indexOf(key)
      if (index === -1) return
      fieldOrder.slice(index + 1).forEach((nextKey) => {
        clearField(nextKey)
        deactivateFieldStep(nextKey)
      })
      deactivateStep(contactStepIds.desktop)
      deactivateStep(contactStepIds.mobile)
      updateModelProgress('mobile')
      updateModelProgress('desktop')
    }


    const handleManufacturerChange = async (value, { auto = false } = {}) => {
      const manufacturer = value || ''
      setFieldValue('manufacturer', manufacturer)
      resetAfter('manufacturer')
      setFieldEnabled('maintype', false)
      setFieldEnabled('firstRegYear', false)
      if (!manufacturer) return

      setFieldLoading('maintype')
      const expected = { manufacturer }
      try {
        const res = await datConfigApi.maintypes(manufacturer)
        if (!isCurrent(expected)) return
        if (!res || res.length === 0) {
          showToast('Keine Modelle gefunden.', 'error')
          return
        }
        setFieldOptions('maintype', res)
        setFieldEnabled('maintype', true)
        showFieldStep('maintype', !auto)
        if (res.length === 1) {
          setFieldValue('maintype', res[0].value)
          await handleMaintypeChange(res[0].value, { auto: true })
        }
      } catch (err) {
        console.warn('Failed to load maintypes', err)
        showToast('Konnte Modelle nicht laden.', 'error')
      }
    }

    const handleMaintypeChange = async (value, { auto = false } = {}) => {
      const maintype = value || ''
      setFieldValue('maintype', maintype)
      resetAfter('maintype')
      setFieldEnabled('firstRegYear', false)
      if (!maintype || !modelState.manufacturer) return

      setFieldLoading('firstRegYear')
      const expected = { manufacturer: modelState.manufacturer, maintype }
      try {
        const res = await datConfigApi.firstRegYears(modelState.manufacturer, maintype)
        if (!isCurrent(expected)) return
        if (!res || res.length === 0) {
          showToast('Keine Erstzulassungen gefunden.', 'error')
          return
        }
        setFieldOptions('firstRegYear', res)
        setFieldEnabled('firstRegYear', true)
        showFieldStep('firstRegYear', !auto)
        if (res.length === 1) {
          setFieldValue('firstRegYear', res[0].value)
          await handleFirstRegYearChange(res[0].value, { auto: true })
        }
      } catch (err) {
        console.warn('Failed to load first reg years', err)
        showToast('Konnte Erstzulassungen nicht laden.', 'error')
      }
    }

    const handleFirstRegYearChange = async (value, { auto = false } = {}) => {
      const firstRegYear = value || ''
      setFieldValue('firstRegYear', firstRegYear)
      resetAfter('firstRegYear')
      setFieldEnabled('bodyDesigns', false)
      if (!firstRegYear || !modelState.manufacturer || !modelState.maintype) return

      setFieldLoading('bodyDesigns')
      const expected = {
        manufacturer: modelState.manufacturer,
        maintype: modelState.maintype,
        firstRegYear,
      }
      try {
        const res = await datConfigApi.bodyDesigns(
          modelState.manufacturer,
          modelState.maintype,
          firstRegYear
        )
        if (!isCurrent(expected)) return
        if (!res || res.length === 0) {
          showToast('Keine Karosserieformen gefunden.', 'error')
          return
        }
        setFieldOptions('bodyDesigns', res)
        setFieldEnabled('bodyDesigns', true)
        showFieldStep('bodyDesigns', !auto)
        if (res.length === 1) {
          setFieldValue('bodyDesigns', res[0].value)
          await handleBodyDesignsChange(res[0].value, { auto: true })
        }
      } catch (err) {
        console.warn('Failed to load body designs', err)
        showToast('Konnte Karosserieformen nicht laden.', 'error')
      }
    }

    const handleBodyDesignsChange = async (value, { auto = false } = {}) => {
      const bodyDesigns = value || ''
      setFieldValue('bodyDesigns', bodyDesigns)
      resetAfter('bodyDesigns')
      setFieldEnabled('fuelTypes', false)
      if (!bodyDesigns || !modelState.manufacturer || !modelState.maintype || !modelState.firstRegYear)
        return

      setFieldLoading('fuelTypes')
      const expected = {
        manufacturer: modelState.manufacturer,
        maintype: modelState.maintype,
        firstRegYear: modelState.firstRegYear,
        bodyDesigns,
      }
      try {
        const res = await datConfigApi.fuelTypes(
          modelState.manufacturer,
          modelState.maintype,
          modelState.firstRegYear,
          bodyDesigns
        )
        if (!isCurrent(expected)) return
        if (!res || res.length === 0) {
          showToast('Keine Kraftstoffe gefunden.', 'error')
          return
        }
        setFieldOptions('fuelTypes', res)
        setFieldEnabled('fuelTypes', true)
        showFieldStep('fuelTypes', !auto)
        if (res.length === 1) {
          setFieldValue('fuelTypes', res[0].value)
          await handleFuelTypesChange(res[0].value, { auto: true })
        }
      } catch (err) {
        console.warn('Failed to load fuel types', err)
        showToast('Konnte Kraftstoffe nicht laden.', 'error')
      }
    }

    const handleFuelTypesChange = async (value, { auto = false } = {}) => {
      const fuelTypes = value || ''
      setFieldValue('fuelTypes', fuelTypes)
      resetAfter('fuelTypes')
      setFieldEnabled('gearboxTypes', false)
      if (
        !fuelTypes ||
        !modelState.manufacturer ||
        !modelState.maintype ||
        !modelState.firstRegYear ||
        !modelState.bodyDesigns
      )
        return

      setFieldLoading('gearboxTypes')
      const expected = {
        manufacturer: modelState.manufacturer,
        maintype: modelState.maintype,
        firstRegYear: modelState.firstRegYear,
        bodyDesigns: modelState.bodyDesigns,
        fuelTypes,
      }
      try {
        const res = await datConfigApi.gearboxTypes(
          modelState.manufacturer,
          modelState.maintype,
          modelState.firstRegYear,
          modelState.bodyDesigns,
          fuelTypes
        )
        if (!isCurrent(expected)) return
        if (!res || res.length === 0) {
          showToast('Keine Getriebe gefunden.', 'error')
          return
        }
        setFieldOptions('gearboxTypes', res)
        setFieldEnabled('gearboxTypes', true)
        showFieldStep('gearboxTypes', !auto)
        if (res.length === 1) {
          setFieldValue('gearboxTypes', res[0].value)
          await handleGearboxTypesChange(res[0].value, { auto: true })
        }
      } catch (err) {
        console.warn('Failed to load gearbox types', err)
        showToast('Konnte Getriebe nicht laden.', 'error')
      }
    }

    const handleGearboxTypesChange = async (value, { auto = false } = {}) => {
      const gearboxTypes = value || ''
      setFieldValue('gearboxTypes', gearboxTypes)
      resetAfter('gearboxTypes')
      setFieldEnabled('enginePower', false)
      if (
        !gearboxTypes ||
        !modelState.manufacturer ||
        !modelState.maintype ||
        !modelState.firstRegYear ||
        !modelState.bodyDesigns ||
        !modelState.fuelTypes
      )
        return

      setFieldLoading('enginePower')
      const expected = {
        manufacturer: modelState.manufacturer,
        maintype: modelState.maintype,
        firstRegYear: modelState.firstRegYear,
        bodyDesigns: modelState.bodyDesigns,
        fuelTypes: modelState.fuelTypes,
        gearboxTypes,
      }
      try {
        const res = await datConfigApi.enginePower(
          modelState.manufacturer,
          modelState.maintype,
          modelState.firstRegYear,
          modelState.bodyDesigns,
          modelState.fuelTypes,
          gearboxTypes
        )
        if (!isCurrent(expected)) return
        if (!res || res.length === 0) {
          showToast('Keine Motorleistungen gefunden.', 'error')
          return
        }
        setFieldOptions('enginePower', res)
        setFieldEnabled('enginePower', true)
        showFieldStep('enginePower', !auto)
        if (res.length === 1) {
          setFieldValue('enginePower', res[0].value)
          await handleEnginePowerChange(res[0].value, { auto: true })
        }
      } catch (err) {
        console.warn('Failed to load engine power', err)
        showToast('Konnte Motorleistungen nicht laden.', 'error')
      }
    }

    const handleEnginePowerChange = async (value, { auto = false } = {}) => {
      const enginePower = value || ''
      setFieldValue('enginePower', enginePower)
      resetAfter('enginePower')
      setFieldEnabled('subtypes', false)
      if (
        !enginePower ||
        !modelState.manufacturer ||
        !modelState.maintype ||
        !modelState.firstRegYear ||
        !modelState.bodyDesigns ||
        !modelState.fuelTypes ||
        !modelState.gearboxTypes
      )
        return

      setFieldLoading('subtypes')
      const expected = {
        manufacturer: modelState.manufacturer,
        maintype: modelState.maintype,
        firstRegYear: modelState.firstRegYear,
        bodyDesigns: modelState.bodyDesigns,
        fuelTypes: modelState.fuelTypes,
        gearboxTypes: modelState.gearboxTypes,
        enginePower,
      }
      try {
        const res = await datConfigApi.subtypes(
          modelState.manufacturer,
          modelState.maintype,
          modelState.firstRegYear,
          modelState.bodyDesigns,
          modelState.fuelTypes,
          modelState.gearboxTypes,
          enginePower
        )
        if (!isCurrent(expected)) return
        if (!res || res.length === 0) {
          showToast('Keine Untermodelle gefunden.', 'error')
          return
        }
        setFieldOptions('subtypes', res)
        setFieldEnabled('subtypes', true)
        showFieldStep('subtypes', !auto)
        if (res.length === 1) {
          setFieldValue('subtypes', res[0].value)
          handleSubtypesChange(res[0].value, { auto: true })
        }
      } catch (err) {
        console.warn('Failed to load subtypes', err)
        showToast('Konnte Untermodelle nicht laden.', 'error')
      }
    }

    const handleSubtypesChange = (value, { auto = false } = {}) => {
      const subtypes = value || ''
      setFieldValue('subtypes', subtypes)
      resetAfter('subtypes')
      if (!subtypes) return
      setFieldEnabled('mileage', true)
      showFieldStep('mileage', !auto)
    }

    const handleMileageChange = (value, { auto = false } = {}) => {
      const mileage = value || ''
      setFieldValue('mileage', mileage)
      resetAfter('mileage')
      if (!mileage) return
      showContactStep(!auto)
    }
    const attachChangeHandler = (key, handler) => {
      getSelectsForField(key).forEach((selectEl) => {
        const onChange = (event) => handler(event.target.value)
        const eventName = selectEl.tagName === 'INPUT' ? 'input' : 'change'
        selectEl.addEventListener(eventName, onChange)
        brandCleanupFns.push(() => selectEl.removeEventListener(eventName, onChange))
      })
    }

    const setupDropdowns = () => {
      const manufacturerSelects = getSelectsForField('manufacturer')
      manufacturerSelects.forEach((selectEl) => {
        const dropdown = enhanceBrandSelect(selectEl, placeholders.manufacturer)
        if (dropdown) {
          brandCleanupFns.push(() => dropdown.cleanup())
        }
      })

      Object.keys(fieldConfig).forEach((key) => {
        if (key === 'manufacturer') return
        const config = fieldConfig[key]
        if (!config) return
        getSelectsForField(key).forEach((selectEl) => {
          if (!(selectEl instanceof HTMLSelectElement)) return
          const controller = enhanceSimpleSelect(selectEl)
          if (controller) {
            brandCleanupFns.push(() => controller.cleanup())
          }
        })
      })

      ;[
        // FIN selects
        'fin-year',
        'fin-mileage',
        'desktop-fin-year',
        'desktop-fin-mileage',
        // HSN/TSN selects
        'hsn-year',
        'hsn-mileage',
        'desktop-hsn-year',
        'desktop-hsn-mileage',
      ].forEach((id) => {
        const controller = enhanceSimpleSelect(document.getElementById(id))
        if (controller) {
          brandCleanupFns.push(() => controller.cleanup())
        }
      })

      ;['desktop-mileage-select', 'mobile-mileage-select'].forEach((id) => {
        const inputEl = document.getElementById(id)
        if (!(inputEl instanceof HTMLInputElement)) return
        const controller = enhanceComboInput(inputEl)
        if (controller) {
          brandCleanupFns.push(() => controller.cleanup())
          try {
            const raw = inputEl.dataset?.options
            const labels = raw ? JSON.parse(raw) : []
            const items = Array.isArray(labels)
              ? labels.map((label) => ({ value: label, label }))
              : []
            controller.setOptions(items)
          } catch (err) {
            console.warn('Failed to parse mileage options', err)
          }
        }
      })
    }

    const loadManufacturers = async () => {
      setFieldLoading('manufacturer')
      try {
        const res = await datConfigApi.manufacturers()
        if (!res || res.length === 0) {
          showToast('Keine Marken gefunden.', 'error')
          return
        }
        setFieldOptions('manufacturer', res)
        setFieldEnabled('manufacturer', true)
        showFieldStep('manufacturer', false)
      } catch (err) {
        console.warn('Failed to load manufacturers', err)
        showToast('Konnte Marken nicht laden.', 'error')
      }
    }

    setupDropdowns()

    attachChangeHandler('manufacturer', handleManufacturerChange)
    attachChangeHandler('maintype', handleMaintypeChange)
    attachChangeHandler('firstRegYear', handleFirstRegYearChange)
    attachChangeHandler('bodyDesigns', handleBodyDesignsChange)
    attachChangeHandler('fuelTypes', handleFuelTypesChange)
    attachChangeHandler('gearboxTypes', handleGearboxTypesChange)
    attachChangeHandler('enginePower', handleEnginePowerChange)
    attachChangeHandler('subtypes', handleSubtypesChange)
    attachChangeHandler('mileage', handleMileageChange)

    setFieldEnabled('manufacturer', true)
    setFieldEnabled('maintype', false)
    setFieldEnabled('firstRegYear', false)
    setFieldEnabled('bodyDesigns', false)
    setFieldEnabled('fuelTypes', false)
    setFieldEnabled('gearboxTypes', false)
    setFieldEnabled('enginePower', false)
    setFieldEnabled('subtypes', false)
    setFieldEnabled('mileage', false)
    showFieldStep('maintype', false)
    showFieldStep('firstRegYear', false)

    loadManufacturers()

    updateModelProgress('mobile')
    updateModelProgress('desktop')
    syncHeroHeight()
  }

  setupBrandModelFlow()
  const submissionCleanup = setupInquirySubmission()
  if (submissionCleanup) {
    cleanupFns.push(submissionCleanup)
  }

  // Expose globals for inline handlers
  window.switchTab = switchTab
  window.switchMobileTab = switchMobileTab
  window.switchDesktopTab = switchDesktopTab
  window.showNextStep = showNextStep
  window.checkEmailAndShowNext = checkEmailAndShowNext
  window.handleFileUpload = handleFileUpload
  window.checkFinInput = checkFinInput
  window.checkHsnTsn = checkHsnTsn
  window.checkHsnTsnDesktop = checkHsnTsnDesktop
  window.checkPhone = checkPhoneMobile // pointing to mobile version for consistency with existing external calls if any

  return () => {
    window.removeEventListener('load', loadHandler)
    window.removeEventListener('resize', syncHeroHeight)
    cleanupFns.forEach((fn) => fn())
    heroObserver?.disconnect?.()
    brandCleanupFns.forEach((fn) => fn())
    ;[
      'switchTab',
      'switchMobileTab',
      'switchDesktopTab',
      'lockContainerHeight',
      'expandContainerHeight',
      'showNextStep',
      'checkEmailAndShowNext',
      'handleFileUpload',
      'checkFinInput',
      'checkHsnTsn',
      'checkHsnTsnDesktop',
      'checkPhone',
    ].forEach((key) => {
      delete window[key]
    })
  }
}

/**
 * Lightweight helper for React/mobile flows: scans a FIN image and returns the detected FIN (or throws).
 * This reuses the same OCR/AI pipeline as the landing page without mutating DOM nodes.
 */
export const scanFinFile = async (file) => {
  if (!file) throw new Error('NO_FILE')
  // Try AI first
  try {
    const aiResult = await tryAiScan(file, 'fin')
    if (aiResult?.payload?.fin) {
      return { fin: aiResult.payload.fin, strategy: aiResult.strategy || 'ai-gpt-nano' }
    }
  } catch (err) {
    console.warn('AI scan failed, falling back to OCR', err)
  }

  const { payload, strategy } = await extractVehicleData(file, 'fin')
  if (payload?.fin) {
    return { fin: payload.fin, strategy }
  }
  throw new Error('FIN_NOT_DETECTED')
}
